import { lazy, Suspense } from "react";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AnimatePresence } from "framer-motion";
import { PageTransition } from "@/components/PageTransition";
import { RouteFallback } from "@/components/RouteFallback";
import { useRouteTiming } from "@/hooks/useRouteTiming";

// Eagerly load the homepage since it's the most common entry point
import Index from "@/pages/Index";

// Lazy load all other pages
const About = lazy(() => import("@/pages/About"));
const Services = lazy(() => import("@/pages/Services"));
const FractionalCFO = lazy(() => import("@/pages/services/FractionalCFO"));
const ServicePage = lazy(() => import("@/pages/services/ServicePage"));
const ServiceLocationPage = lazy(() => import("@/pages/services/ServiceLocationPage"));
const Blog = lazy(() => import("@/pages/Blog"));
const BlogPost = lazy(() => import("@/pages/BlogPost"));
const Resources = lazy(() => import("@/pages/Resources"));
const Contact = lazy(() => import("@/pages/Contact"));
const Industries = lazy(() => import("@/pages/Industries"));
const FAQ = lazy(() => import("@/pages/FAQ"));
const AccountantCostGuide = lazy(() => import("@/pages/resources/AccountantCostGuide"));
const Calculators = lazy(() => import("@/pages/Calculators"));
const CalculatorPage = lazy(() => import("@/pages/calculators/CalculatorPage"));
const HRServices = lazy(() => import("@/pages/HRServices"));
const AdminLogin = lazy(() => import("@/pages/AdminLogin"));
const AdminBlog = lazy(() => import("@/pages/AdminBlog"));
const AdminEnquiries = lazy(() => import("@/pages/AdminEnquiries"));
const NotFound = lazy(() => import("@/pages/NotFound"));
const LandingPage = lazy(() => import("@/pages/LandingPage"));
const AdminCampaigns = lazy(() => import("@/pages/AdminCampaigns"));
const AdminDashboard = lazy(() => import("@/pages/AdminDashboard"));
const AdminFAQ = lazy(() => import("@/pages/AdminFAQ"));
const AdminMarketing = lazy(() => import("@/pages/AdminMarketing"));
const AdminUsers = lazy(() => import("@/pages/AdminUsers"));
const AdminBookingClicks = lazy(() => import("@/pages/AdminBookingClicks"));
const AdminConversions = lazy(() => import("@/pages/AdminConversions"));
const AdminLeadMagnets = lazy(() => import("@/pages/AdminLeadMagnets"));
const AdminSEO = lazy(() => import("@/pages/AdminSEO"));
const ResetPassword = lazy(() => import("@/pages/ResetPassword"));
const PrivacyPolicy = lazy(() => import("@/pages/PrivacyPolicy"));
const PrivacyRights = lazy(() => import("@/pages/PrivacyRights"));
const TermsOfService = lazy(() => import("@/pages/TermsOfService"));

// Resource guide pages
const NewBusinessChecklist = lazy(() => import("@/pages/resources/NewBusinessChecklist"));
const PayrollGuide = lazy(() => import("@/pages/resources/PayrollGuide"));
const VATRegistrationGuide = lazy(() => import("@/pages/resources/VATRegistrationGuide"));
const TaxCalendar = lazy(() => import("@/pages/resources/TaxCalendar"));
const MTDGuide = lazy(() => import("@/pages/resources/MTDGuide"));
const DirectorsLoanGuide = lazy(() => import("@/pages/resources/DirectorsLoanGuide"));
const PensionChecklist = lazy(() => import("@/pages/resources/PensionChecklist"));
const YearEndChecklist = lazy(() => import("@/pages/resources/YearEndChecklist"));
const SoleTraderGuide = lazy(() => import("@/pages/resources/SoleTraderGuide"));
const LimitedCompanyGuide = lazy(() => import("@/pages/resources/LimitedCompanyGuide"));

// Area pages
const AreasIndex = lazy(() => import("@/pages/areas/AreasIndex"));
const NewForest = lazy(() => import("@/pages/areas/NewForest"));
const Salisbury = lazy(() => import("@/pages/areas/Salisbury"));
const Wimborne = lazy(() => import("@/pages/areas/WimborneBournemouth"));
const TownPage = lazy(() => import("@/pages/areas/TownPage"));

// MTD hub
const MTDHub = lazy(() => import("@/pages/mtd/MTDHub"));
const MTDLandlords = lazy(() => import("@/pages/mtd/MTDLandlords"));
const MTDSelfEmployed = lazy(() => import("@/pages/mtd/MTDSelfEmployed"));
const MTDSoftware = lazy(() => import("@/pages/mtd/MTDSoftware"));

// Lazy load AdminLayout separately since it's only needed for admin routes
const AdminLayout = lazy(() => import("@/components/admin/AdminLayout").then(m => ({ default: m.AdminLayout })));

export function AnimatedRoutes() {
  const location = useLocation();
  useRouteTiming();

  return (
    <AnimatePresence mode="wait">
      <Suspense fallback={<RouteFallback />}>
        <Routes location={location} key={location.pathname}>
          <Route path="/" element={<PageTransition><Index /></PageTransition>} />
          <Route path="/about" element={<PageTransition><About /></PageTransition>} />
          <Route path="/services" element={<PageTransition><Services /></PageTransition>} />
          <Route path="/services/fractional-cfo-finance-director" element={<PageTransition><FractionalCFO /></PageTransition>} />
          {/* Legacy WordPress service paths */}
          <Route path="/services/accounts" element={<Navigate to="/services/year-end-accounts" replace />} />
          <Route path="/services/accounts-preparation" element={<Navigate to="/services/year-end-accounts" replace />} />
          <Route path="/services/payroll" element={<Navigate to="/services/payroll-and-pensions" replace />} />
          <Route path="/services/pensions" element={<Navigate to="/services/payroll-and-pensions" replace />} />
          <Route path="/services/vat" element={<Navigate to="/services/vat-returns" replace />} />
          <Route path="/services/tax" element={<Navigate to="/services/tax-planning" replace />} />
          <Route path="/services/tax-returns" element={<Navigate to="/services/self-assessment" replace />} />
          <Route path="/services/:slug" element={<PageTransition><ServicePage /></PageTransition>} />
          <Route path="/services/:slug/:location" element={<PageTransition><ServiceLocationPage /></PageTransition>} />
          <Route path="/2025/*" element={<Navigate to="/blog" replace />} />
          <Route path="/2024/*" element={<Navigate to="/blog" replace />} />
          <Route path="/2023/*" element={<Navigate to="/blog" replace />} />
          <Route path="/blog" element={<PageTransition><Blog /></PageTransition>} />
          <Route path="/blog/:slug" element={<PageTransition><BlogPost /></PageTransition>} />
          <Route path="/resources" element={<PageTransition><Resources /></PageTransition>} />
          <Route path="/resources/new-business-checklist" element={<PageTransition><NewBusinessChecklist /></PageTransition>} />
          <Route path="/resources/payroll-guide" element={<PageTransition><PayrollGuide /></PageTransition>} />
          <Route path="/resources/vat-registration-guide" element={<PageTransition><VATRegistrationGuide /></PageTransition>} />
          <Route path="/resources/tax-calendar" element={<PageTransition><TaxCalendar /></PageTransition>} />
          <Route path="/resources/mtd-guide" element={<PageTransition><MTDGuide /></PageTransition>} />
          <Route path="/resources/directors-loan-guide" element={<PageTransition><DirectorsLoanGuide /></PageTransition>} />
          <Route path="/resources/pension-checklist" element={<PageTransition><PensionChecklist /></PageTransition>} />
          <Route path="/resources/year-end-checklist" element={<PageTransition><YearEndChecklist /></PageTransition>} />
          <Route path="/resources/sole-trader-guide" element={<PageTransition><SoleTraderGuide /></PageTransition>} />
          <Route path="/resources/accountant-cost-guide" element={<PageTransition><AccountantCostGuide /></PageTransition>} />
          <Route path="/resources/limited-company-guide" element={<PageTransition><LimitedCompanyGuide /></PageTransition>} />
          <Route path="/contact" element={<PageTransition><Contact /></PageTransition>} />
          <Route path="/industries" element={<PageTransition><Industries /></PageTransition>} />
          <Route path="/faq" element={<PageTransition><FAQ /></PageTransition>} />
          <Route path="/calculators" element={<PageTransition><Calculators /></PageTransition>} />
          <Route path="/calculators/:slug" element={<PageTransition><CalculatorPage /></PageTransition>} />
          <Route path="/hr-services" element={<PageTransition><HRServices /></PageTransition>} />
          <Route path="/privacy-policy" element={<PageTransition><PrivacyPolicy /></PageTransition>} />
          <Route path="/privacy-rights" element={<PageTransition><PrivacyRights /></PageTransition>} />
          <Route path="/terms-of-service" element={<PageTransition><TermsOfService /></PageTransition>} />
          <Route path="/areas" element={<PageTransition><AreasIndex /></PageTransition>} />
          <Route path="/areas/new-forest" element={<PageTransition><NewForest /></PageTransition>} />
          <Route path="/areas/salisbury" element={<PageTransition><Salisbury /></PageTransition>} />
          <Route path="/areas/wimborne" element={<PageTransition><Wimborne /></PageTransition>} />
          {/* Retired area pages: folded into the local hubs after the Salisbury move. */}
          <Route path="/areas/bournemouth-poole" element={<Navigate to="/areas/wimborne" replace />} />
          <Route path="/areas/london" element={<Navigate to="/areas" replace />} />
          <Route path="/areas/reading-henley" element={<Navigate to="/areas" replace />} />
          <Route path="/areas/wimborne-bournemouth" element={<Navigate to="/areas/wimborne" replace />} />
          {/* Town pages resolve from the :slug param; unknown towns fall back to /areas. */}
          <Route path="/areas/:slug" element={<PageTransition><TownPage /></PageTransition>} />

          <Route path="/mtd-itsa" element={<PageTransition><MTDHub /></PageTransition>} />
          <Route path="/mtd-itsa/landlords" element={<PageTransition><MTDLandlords /></PageTransition>} />
          <Route path="/mtd-itsa/self-employed" element={<PageTransition><MTDSelfEmployed /></PageTransition>} />
          <Route path="/mtd-itsa/software-checklist" element={<PageTransition><MTDSoftware /></PageTransition>} />
          <Route path="/landing" element={<LandingPage />} />
          <Route path="/admin/login" element={<AdminLogin />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/admin" element={<AdminLayout><AdminDashboard /></AdminLayout>} />
          <Route path="/admin/blog" element={<AdminLayout><AdminBlog /></AdminLayout>} />
          <Route path="/admin/enquiries" element={<AdminLayout><AdminEnquiries /></AdminLayout>} />
          <Route path="/admin/campaigns" element={<AdminLayout><AdminCampaigns /></AdminLayout>} />
          <Route path="/admin/faqs" element={<AdminLayout><AdminFAQ /></AdminLayout>} />
          <Route path="/admin/users" element={<AdminLayout><AdminUsers /></AdminLayout>} />
          <Route path="/admin/marketing" element={<AdminLayout><AdminMarketing /></AdminLayout>} />
          <Route path="/admin/booking-clicks" element={<AdminLayout><AdminBookingClicks /></AdminLayout>} />
          <Route path="/admin/conversions" element={<AdminLayout><AdminConversions /></AdminLayout>} />
          <Route path="/admin/lead-magnets" element={<AdminLayout><AdminLeadMagnets /></AdminLayout>} />
          <Route path="/admin/seo" element={<AdminLayout><AdminSEO /></AdminLayout>} />
          <Route path="*" element={<PageTransition><NotFound /></PageTransition>} />
        </Routes>
      </Suspense>
    </AnimatePresence>
  );
}
